/**
 * Loot-table catalog — the single source of truth is `loot.store`
 * (repo root); this module imports its generated projection
 * `loot.data.ts` (do not edit — save in the knobs app's Loot tab or run
 * `node tools/loot-store.js`).
 *
 * A loot table is what any creature's death rolls (the boss is just a
 * creature with a better table) and what the base-room sack is rolled from
 * at generation (design/loot.md). One mechanic, three consumers — the table
 * is pure data: how many times the pool rolls (itemsMin..itemsMax, inclusive),
 * the pool of (item, probability) rows summing to AT MOST 100 (the remainder
 * is the nothing probability), per-row quantity
 * bounds, and the drops_bones flag (logic, not a resource — ghosts have no
 * bones).
 *
 * The roll itself (the procedure, the loot fork, ground piles, the sack,
 * pickup) lands in the sim slice (loot.md build 3); until then this is the
 * data surface the store, the Loot tab and the cross-store references ride
 * on: npc.store carries a creature's table, floors.store an optional
 * sack-table reference. Pool rows name an item by (class, kind) — a world
 * item is a (class, kind) pair: equipment kind (equipment.store) or
 * consumable id (consumables.store).
 */
import { LOOT_DATA } from "./loot.data.js";
import { ITEMS } from "./items.js";
import { CONSUMABLES } from "./consumables.js";

/** The item classes a pool row may name (a world item is a (class, kind)
 *  pair): equipment = an equipment.store id, consumable = a consumables.store
 *  id. */
export const LOOT_ITEM_CLASSES = ["equipment", "consumable"] as const;
export type LootItemClass = (typeof LOOT_ITEM_CLASSES)[number];

export interface LootRow {
  /** Which catalog the kind is an id in. */
  itemClass: LootItemClass;
  /** The item id in that catalog (equipment.store kind / consumables.store id). */
  kind: number;
  /** Probability out of 100; the table's rows sum to AT MOST 100 — the
   *  remainder (100 − sum) is the chance a roll attempt drops nothing. */
  probability: number;
  /** How many of this item spawn per roll of it (inclusive, >= 1). */
  qMin: number;
  /** Inclusive upper quantity bound (>= qMin). */
  qMax: number;
}

export interface LootTableDef {
  /** Catalog id — the permanent loot.store id (wire id; npc.store /
   *  floors.store reference it; the catalog may have gaps). */
  id: number;
  /** Display name (the Loot tab's list, the NPC / Floors tab pickers). */
  name: string;
  /** How many times the pool rolls when the table is used (inclusive, >= 1 —
   *  at least one roll attempt; a roll can still drop nothing via the pool's
   *  nothing probability). */
  itemsMin: number;
  /** Inclusive upper roll-count bound (>= itemsMin). */
  itemsMax: number;
  /** Whether the death leaves the pile-of-bones decal (default true — ghosts
   *  have no bones; logic, not a resource). */
  dropsBones: boolean;
  /** The pool — probabilities sum to at most 100 (the remainder is the
   *  nothing probability). Empty pools drop nothing, retaining table identity
   *  and the bones flag after a catalog deletion. */
  items: LootRow[];
}

/**
 * All loot tables, validated at load with the same discipline the other
 * catalogs use: a hand-edited loot.data.ts must fail loudly, not
 * type-trick its way past the checks. `equipmentKinds` / `consumableKinds`
 * are injectable (tests pass explicit sets — a unit test never depends on
 * what a human authored); the defaults are the committed catalogs.
 */
export function buildLootCatalog(
  data: readonly unknown[] = LOOT_DATA,
  equipmentKinds?: ReadonlySet<number>,
  consumableKinds?: ReadonlySet<number>,
): readonly LootTableDef[] {
  const equipIds = equipmentKinds ?? new Set(ITEMS.map((i) => i.kind));
  const consIds = consumableKinds ?? new Set(CONSUMABLES.map((c) => c.id));
  const ids = new Set<number>();
  const out: LootTableDef[] = [];
  for (const r of data) {
    const raw = r as {
      id?: unknown; name?: unknown; itemsMin?: unknown; itemsMax?: unknown;
      dropsBones?: unknown; items?: unknown;
    };
    if (!Number.isInteger(raw.id) || (raw.id as number) < 0) {
      throw new Error(`loot.data: bad id ${String(raw.id)} (${String(raw.name)})`);
    }
    const id = raw.id as number;
    if (ids.has(id)) throw new Error(`loot.data: duplicate id ${id} (${String(raw.name)})`);
    ids.add(id);
    if (typeof raw.name !== "string" || raw.name === "") {
      throw new Error(`loot.data: id ${id}: missing name`);
    }
    const name = raw.name;
    if (!Number.isInteger(raw.itemsMin) || (raw.itemsMin as number) < 1) {
      throw new Error(`loot.data: ${name}: itemsMin must be an integer >= 1`);
    }
    if (!Number.isInteger(raw.itemsMax) || (raw.itemsMax as number) < (raw.itemsMin as number)) {
      throw new Error(`loot.data: ${name}: itemsMax must be an integer >= itemsMin`);
    }
    if (raw.dropsBones !== undefined && raw.dropsBones !== null && typeof raw.dropsBones !== "boolean") {
      throw new Error(`loot.data: ${name}: dropsBones must be a boolean (absent = true)`);
    }
    if (!Array.isArray(raw.items)) {
      throw new Error(`loot.data: ${name}: the item pool must be an array`);
    }
    const rows: LootRow[] = [];
    const seen = new Set<string>();
    let total = 0;
    for (const row of raw.items) {
      const rr = row as {
        itemClass?: unknown; kind?: unknown; probability?: unknown;
        qMin?: unknown; qMax?: unknown;
      };
      if (typeof rr.itemClass !== "string" || !(LOOT_ITEM_CLASSES as readonly string[]).includes(rr.itemClass)) {
        throw new Error(`loot.data: ${name}: bad item class ${String(rr.itemClass)} (allowed: ${LOOT_ITEM_CLASSES.join(" | ")})`);
      }
      const itemClass = rr.itemClass as LootItemClass;
      if (!Number.isInteger(rr.kind) || (rr.kind as number) < 0) {
        throw new Error(`loot.data: ${name}: bad row kind ${String(rr.kind)}`);
      }
      const kind = rr.kind as number;
      if (itemClass === "equipment" && !equipIds.has(kind)) {
        throw new Error(`loot.data: ${name}: equipment kind ${kind} is not in the equipment catalog (equipment.store) — fix the reference or the store`);
      }
      if (itemClass === "consumable" && !consIds.has(kind)) {
        throw new Error(`loot.data: ${name}: consumable kind ${kind} is not in the consumable catalog (consumables.store) — fix the reference or the store`);
      }
      if (!Number.isInteger(rr.probability) || (rr.probability as number) < 1 || (rr.probability as number) > 100) {
        throw new Error(`loot.data: ${name}: probability must be an integer 1-100`);
      }
      if (!Number.isInteger(rr.qMin) || (rr.qMin as number) < 1) {
        throw new Error(`loot.data: ${name}: qMin must be an integer >= 1`);
      }
      if (!Number.isInteger(rr.qMax) || (rr.qMax as number) < (rr.qMin as number)) {
        throw new Error(`loot.data: ${name}: qMax must be an integer >= qMin`);
      }
      const key = `${itemClass}:${kind}`;
      if (seen.has(key)) throw new Error(`loot.data: ${name}: duplicate row ${key}`);
      seen.add(key);
      total += rr.probability as number;
      rows.push({
        itemClass,
        kind,
        probability: rr.probability as number,
        qMin: rr.qMin as number,
        qMax: rr.qMax as number,
      });
    }
    if (total > 100) {
      throw new Error(`loot.data: ${name}: item probabilities must sum to at most 100 (got ${total}) — the remainder is the nothing probability`);
    }
    out.push({
      id,
      name,
      itemsMin: raw.itemsMin as number,
      itemsMax: raw.itemsMax as number,
      dropsBones: raw.dropsBones === undefined || raw.dropsBones === null ? true : raw.dropsBones,
      items: rows,
    });
  }
  return out;
}

/** All loot tables, by catalog id (may have gaps — permanent ids). */
export const LOOT: readonly LootTableDef[] = buildLootCatalog();

const BY_ID = new Map(LOOT.map((t) => [t.id, t]));

/** Catalog lookup; undefined for unknown ids (the sim rejects those). */
export function lootTableById(id: number): LootTableDef | undefined {
  return BY_ID.get(id);
}
